import { FeatureClass, RagStatus } from "../models/enums";
import { ConductingSegmentProperties, TransformerProperties } from "../models/featureProperties";
import { ConnectionPoint, ExistingNetworkAsset, GeoServerCable } from "../models/models";
import { appConfig } from "../services/configService";

export const useRagStyle = function () {
  const { redColor, amberColor, greenColor, unknownColor, cableTypeIdBlacklist } = appConfig;
  //const { powerTransformers } = useAppSelector(selectExistingNetwork);

  /**
   * takes a numeric property and evaluates the RAG color based on an upper and lower theshold
   * @param property numeric value for the RAG property
   * @param lowValue lower threshold below which RAG status is green
   * @param highValue upper threshold above which RAG status is red
   * @returns the appropriate RAG color
   */
  const ragCalc = (property: number | null, lowValue: number, highValue: number): string => {
    if (property === null) return amberColor;
    return property <= lowValue ? greenColor : property <= highValue ? amberColor : redColor;
  };
  const getMainsRag = (
    geoServerCable: GeoServerCable,
    totalKva: number,
    maxConsumerPhase: number
  ) => {
    const {
      TransformerMaxUtilisationPercent: transformerUtilisation,
      MaximumBranchUtilisationUpstreamPercent: UpstreamCableUtilisation,
      MaxUtilisationPercent: cableUtilisation,
      MaxVoltDropPercent: voltDrop,
      MaxVoltRisePercent: voltRise,
      SourceLoopOperatingImpedance: sourceLoopImpedance,
      TransformerRatingkVA: transformerRatingKva,
      SystemVoltage: systemVoltage,
      CableRatingAmps: cableRatingAmps,
      NumberOfPhases: cablePhasingString,
      CableType: cableType,
      TotalAtcEnergyConsumption: atcSubStationConsumption,
    } = geoServerCable;

    const isSinglePhaseConnection = maxConsumerPhase === 1;

    const newTransformerUtilisation =
      transformerUtilisation === null || transformerRatingKva === null
        ? null
        : transformerUtilisation + (totalKva / transformerRatingKva) * 100;

    const newUpstreamCableUtilisation =
      UpstreamCableUtilisation === null || systemVoltage === null || cableRatingAmps === null
        ? null
        : isSinglePhaseConnection
          ? UpstreamCableUtilisation + ((totalKva * (1000 / systemVoltage)) / cableRatingAmps) * 100
          : UpstreamCableUtilisation +
            (((totalKva / 3) * 1000) / systemVoltage / cableRatingAmps) * 100;

    const newCableUtilisation =
      cableUtilisation === null ||
      systemVoltage === null ||
      cableRatingAmps === null ||
      cableUtilisation === undefined ||
      systemVoltage === undefined ||
      cableRatingAmps === undefined
        ? null
        : isSinglePhaseConnection
          ? cableUtilisation + ((totalKva * (1000 / systemVoltage)) / cableRatingAmps) * 100
          : cableUtilisation + (((totalKva / 3) * 1000) / systemVoltage / cableRatingAmps) * 100;

    let cablePhasing =
      cablePhasingString?.toLowerCase() === "single"
        ? 1
        : cablePhasingString?.toLowerCase() === "split"
          ? 2
          : 3;

    const transformerUtilisationRAG = ragCalc(newTransformerUtilisation, 66, 90);
    const UpstreamCableUtilisationRAG = ragCalc(newUpstreamCableUtilisation, 66, 90);
    const cableUtilisationRag = ragCalc(newCableUtilisation, 66, 90);
    const voltDropRAG = ragCalc(voltDrop, 3.83, 4.79);
    const voltRiseRAG = ragCalc(voltRise, 1.4375, 1.4375);
    const sourceLoopImpedanceRAG = ragCalc(sourceLoopImpedance, 144, 336);
    const phaseRAG = ragCalc(maxConsumerPhase, cablePhasing, cablePhasing);
    const atcRAG = ragCalc(atcSubStationConsumption, 69, 69);
    const cableTypeRAG = cableTypeIdBlacklist.includes(cableType) ? RagStatus.red : RagStatus.green;

    return [
      transformerUtilisationRAG,
      UpstreamCableUtilisationRAG,
      cableUtilisationRag,
      voltDropRAG,
      voltRiseRAG,
      sourceLoopImpedanceRAG,
      phaseRAG,
      cableTypeRAG,
      atcRAG,
    ].includes(RagStatus.unknown)
      ? unknownColor
      : [
            transformerUtilisationRAG,
            UpstreamCableUtilisationRAG,
            cableUtilisationRag,
            voltDropRAG,
            voltRiseRAG,
            sourceLoopImpedanceRAG,
            phaseRAG,
            cableTypeRAG,
            atcRAG,
          ].includes(RagStatus.red)
        ? redColor
        : [
              transformerUtilisationRAG,
              UpstreamCableUtilisationRAG,
              cableUtilisationRag,
              voltDropRAG,
              voltRiseRAG,
              sourceLoopImpedanceRAG,
              phaseRAG,
              cableTypeRAG,
              atcRAG,
            ].includes(RagStatus.amber)
          ? amberColor
          : [
                transformerUtilisationRAG,
                UpstreamCableUtilisationRAG,
                cableUtilisationRag,
                voltDropRAG,
                voltRiseRAG,
                sourceLoopImpedanceRAG,
                phaseRAG,
                cableTypeRAG,
                atcRAG,
              ].includes(RagStatus.green)
            ? greenColor
            : "";
  };
  const getTransformerRag = (
    transformerProperties: TransformerProperties,
    totalKva: number
  ): string => {
    const {
      TransformerMaxUtilisationPercent: utilisation,
      TotalAtcEnergyConsumption: atcConsumption,
      NameplateRating: rating,
    } = transformerProperties;

    if (atcConsumption !== null && atcConsumption !== undefined && atcConsumption > 69) {
      return redColor;
    }

    if (utilisation === null || utilisation === undefined) {
      return unknownColor;
    }

    const dynamicUtilisation = utilisation + (totalKva / rating) * 100;

    switch (true) {
      case dynamicUtilisation <= 66:
        return greenColor;
      case dynamicUtilisation > 66 && dynamicUtilisation <= 90:
        return amberColor;
      case dynamicUtilisation > 90:
        return redColor;
      default:
        return unknownColor;
    }
  };

  const getMaxConsumerPhase = (connectionPoints: ConnectionPoint[]) => {
    const connectionPointPhases = connectionPoints.map((x) => {
      let phase;
      switch (x.phases.toLowerCase()) {
        case "single": {
          phase = 1;
          break;
        }
        case "three": {
          phase = 3;
          break;
        }
        default:
          phase = 0;
      }
      return phase;
    });

    return Math.max(...connectionPointPhases);
  };

  const getAssetStyles = (
    existingNetworkAssets: ExistingNetworkAsset[],
    connectionPoints: ConnectionPoint[],
    totalKva: number
  ) => {
    const assets: ExistingNetworkAsset[] = [];
    const maxConsumerPhase = getMaxConsumerPhase(connectionPoints);

    existingNetworkAssets.forEach((asset) => {
      if (asset.properties) {
        const assetProperties = asset.properties as ConductingSegmentProperties;

        const isTransformer =
          assetProperties.Class === FeatureClass.substationGmt ||
          assetProperties.Class === FeatureClass.substationPmt;

        const ragColor = isTransformer
          ? getTransformerRag(asset.properties as TransformerProperties, totalKva)
          : getMainsRag(asset.properties as GeoServerCable, totalKva, maxConsumerPhase);

        asset.properties = { ...asset.properties, ...{ color: ragColor } };
        assets.push(asset);
      } else assets.push(asset);
    });
    return assets;
  };

  return { ragCalc, getMainsRag, getTransformerRag, getAssetStyles };
};
